import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import { useAuth, isWriter, isAdmin } from "@/context/AuthContext";
import { PageHeader, SourceLink, EmptyState } from "@/components/primitives";
import { Modal, Field, inputCls, PrimaryButton, GhostButton } from "@/components/Modal";
import { StatusBadge } from "@/components/StatusBadge";
import { AudienceSelector } from "@/components/AudienceSelector";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";

export default function People() {
  const { currentId } = useProjects();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("Draft");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", role_title: "", department: "", state: "Draft", source_url: "", audiences: ["internal"] });

  const load = useCallback(async () => {
    if (!currentId) return;
    setLoading(true);
    try { const { data } = await api.get(`/api/projects/${currentId}/credits`); setItems(data); }
    finally { setLoading(false); }
  }, [currentId]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim() || !form.role_title.trim()) { toast.error("Name and role required"); return; }
    try { await api.post(`/api/projects/${currentId}/credits`, { ...form, source_url: form.source_url || null }); toast.success("Credit added"); setCreating(false); setForm({ name: "", role_title: "", department: "", state: "Draft", source_url: "", audiences: ["internal"] }); load(); }
    catch { toast.error("Create failed"); }
  };
  const updateAudiences = async (c, auds) => {
    try { await api.put(`/api/credits/${c.id}`, { audiences: auds }); setItems((prev) => prev.map((x) => (x.id === c.id ? { ...x, audiences: auds } : x))); }
    catch { toast.error("Failed"); }
  };
  const toggleState = async (c) => {
    const next = c.state === "Approved" ? "Draft" : "Approved";
    try { await api.put(`/api/credits/${c.id}`, { state: next }); toast.success(`Marked ${next}`); load(); } catch { toast.error("Failed"); }
  };
  const remove = async (c) => {
    if (!window.confirm("Delete credit?")) return;
    try { await api.delete(`/api/credits/${c.id}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); }
  };

  const filtered = items.filter((c) => c.state === tab);

  if (!currentId) return <div className="cm-label animate-pulse">Loading…</div>;

  return (
    <div data-testid="people-page">
      <PageHeader
        eyebrow="People & Credits"
        title="Credits"
        description="Source-linked draft vs approved credits. No personnel are invented — add names only from confirmed source documents."
      >
        {isWriter(user) && <PrimaryButton data-testid="people-add-btn" onClick={() => setCreating(true)}><span className="inline-flex items-center gap-1"><Plus size={14} /> Add credit</span></PrimaryButton>}
      </PageHeader>

      <div className="mb-6 flex gap-2">
        {["Draft", "Approved"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`people-tab-${t.toLowerCase()}`}
            className={`rounded-sm border px-4 py-2 text-sm transition-colors ${tab === t ? "border-[#F59E0B] bg-[rgba(245,158,11,0.12)] text-[#F59E0B]" : "border-[#2C2C2A] bg-[#1A1A18] text-[#A1A19D] hover:text-[#F9F9F8]"}`}>
            {t} ({items.filter((c) => c.state === t).length})
          </button>
        ))}
      </div>

      {loading ? <div className="cm-label animate-pulse">Loading…</div> : filtered.length === 0 ? (
        <EmptyState title={`No ${tab.toLowerCase()} credits`} description="Credits will appear here once added from confirmed sources." testid="people-empty" />
      ) : (
        <div className="overflow-x-auto border border-[#2C2C2A]">
          <table className="w-full min-w-[640px] text-sm">
            <thead><tr className="border-b border-[#2C2C2A] text-left">
              <th className="cm-label p-3">Name</th><th className="cm-label p-3">Credit</th><th className="cm-label p-3">Department</th><th className="cm-label p-3">State</th><th className="cm-label p-3">Audiences</th><th className="cm-label p-3">Source</th><th className="cm-label p-3 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} className="border-b border-[#2C2C2A] last:border-0" data-testid={`credit-row-${c.id}`}>
                  <td className="p-3 text-[#F9F9F8]">{c.name}</td>
                  <td className="p-3 text-[#F9F9F8]">{c.role_title}</td>
                  <td className="p-3 text-[#A1A19D]">{c.department || "—"}</td>
                  <td className="p-3"><StatusBadge status={c.state} /></td>
                  <td className="p-3">
                    {isWriter(user)
                      ? <AudienceSelector label="" value={c.audiences || ["internal"]} onChange={(a) => updateAudiences(c, a)} />
                      : <div className="flex gap-1">{(c.audiences || ["internal"]).map((a) => <StatusBadge key={a} status={a} />)}</div>}
                  </td>
                  <td className="p-3"><SourceLink url={c.source_url} /></td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-3">
                      {isWriter(user) && <button onClick={() => toggleState(c)} className="text-xs text-[#F59E0B] hover:underline">{c.state === "Approved" ? "Unapprove" : "Approve"}</button>}
                      {isAdmin(user) && <button onClick={() => remove(c)} className="text-[#A1A19D] hover:text-[#e07a7a]"><Trash size={14} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={creating} onClose={() => setCreating(false)} title="Add credit" testid="people-create-modal">
        <Field label="Name"><input className={inputCls} data-testid="credit-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
        <Field label="Credit / role title"><input className={inputCls} data-testid="credit-role" value={form.role_title} onChange={(e) => setForm({ ...form, role_title: e.target.value })} /></Field>
        <Field label="Department"><input className={inputCls} value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} /></Field>
        <Field label="State"><select className={inputCls} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })}><option>Draft</option><option>Approved</option></select></Field>
        <Field label="Source URL"><input className={inputCls} value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} /></Field>
        <div className="mb-4"><AudienceSelector value={form.audiences} onChange={(a) => setForm({ ...form, audiences: a })} /></div>
        <div className="flex justify-end gap-2"><GhostButton onClick={() => setCreating(false)}>Cancel</GhostButton><PrimaryButton data-testid="credit-save" onClick={create}>Add</PrimaryButton></div>
      </Modal>
    </div>
  );
}
